import type { ConversationDto, ConversationRowDto, DocumentDto, ProjectDto, SkillDto, WorkspaceDto, WorkspaceMemberDto } from '@dbrain/shared';
import {
  Avatar,
  AvatarStack,
  Badge,
  Button,
  ConfirmDialog,
  Dialog,
  EmptyState,
  Field,
  Icons,
  Menu,
  MenuItem,
  MenuSeparator,
  PageLoading,
  RadioCard,
  Skeleton,
  Switch,
  TabPanel,
  Tabs,
  TextArea,
  TextInput,
  toneOf,
} from '@dbrain/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState, type DragEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useCapabilities } from '../experience.js';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router';
import { capKeys, useSkills } from '../api/capabilities.js';
import { api } from '../api/client.js';
import { docKeys, uploadDocument, useRefreshDocuments } from '../api/documents.js';
import { keys, useActivity, useConversations, useMe, useMembers, useProjects, useWorkspace, useWorkspaceMembers } from '../api/hooks.js';
import { formatSize } from '../components/chat/ChatParts.js';
import { DocumentPreview, DocumentState, documentSize, KindBadge } from '../components/documents/DocumentParts.js';
import { ErrorAlert } from '../components/ErrorAlert.js';
import { SandboxPanel } from '../components/sandbox/SandboxPanel.js';
import { SkillAddDialog } from '../components/skills/SkillAddDialog.js';
import { ProfileIcon, statusOf, StatusBadge, useTimes } from '../components/status.js';
import { homeOf, useExperience, useMeta } from '../experience.js';
import { useRowDetail } from './WorkspacesPage.js';

/**
 * One workspace (docs/design/mockups/CalismaAlani.dc.html): its name, line
 * and members at the top with "Davet et" and "Yeni sohbet", then its parts
 * as tabs — conversations, projects, documents, skills, members, settings
 * (and, in a code workspace, its environment). Every empty part says what it
 * is for and offers one thing to do; new projects and invitations open in
 * a dialog.
 */
type Tab = 'conversations' | 'projects' | 'environment' | 'documents' | 'skills' | 'members' | 'settings';
const TABS: Tab[] = ['conversations', 'projects', 'environment', 'documents', 'skills', 'members', 'settings'];
const TABS_ID = 'workspace-tabs';

function useWorkspaceDocuments(workspaceId: string) {
  return useQuery({
    queryKey: [...docKeys.all, 'workspace', workspaceId],
    queryFn: () => api<DocumentDto[]>('GET', `/api/documents?scope=workspace&workspaceId=${workspaceId}&limit=200`),
    refetchInterval: (query) => ((query.state.data ?? []).some((d) => d.status === 'queued' || d.status === 'processing') ? 2_000 : false),
  });
}

function useNewConversation(workspace: WorkspaceDto) {
  const navigate = useNavigate();
  return useMutation({
    mutationFn: (projectId?: string) => api<ConversationDto>('POST', `/api/workspaces/${workspace.id}/conversations`, projectId ? { projectId } : {}),
    onSuccess: (conversation) => void navigate(`/c/${conversation.id}`),
  });
}

/** "Yeni proje": a name and, in a code workspace, its folder (QW-715). */
function ProjectDialog({ workspace, onClose }: { workspace: WorkspaceDto; onClose: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [name, setName] = useState('');
  const [rootPath, setRootPath] = useState('');
  const create = useMutation({
    mutationFn: () => api<ProjectDto>('POST', `/api/workspaces/${workspace.id}/projects`, { name: name.trim(), ...(workspace.kind === 'code' && rootPath.trim() ? { rootPath: rootPath.trim() } : {}) }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.projects(workspace.id) });
      void queryClient.invalidateQueries({ queryKey: keys.dashboard });
      void queryClient.invalidateQueries({ queryKey: keys.workspaceOverview });
      onClose();
    },
  });
  return (
    <Dialog
      title={t('workspace.projects.new')}
      closeLabel={t('common.close')}
      onClose={onClose}
      data-testid="project-dialog"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" form="project-form" busy={create.isPending} disabled={!name.trim()} data-testid="project-submit">
            {t('common.create')}
          </Button>
        </>
      }
    >
      <form
        id="project-form"
        className="dialog-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim()) create.mutate();
        }}
      >
        <p className="muted">{t(workspace.kind === 'code' ? 'workspace.projects.codeHint' : 'workspace.projects.chatHint')}</p>
        <Field label={t('workspace.projects.name')}>
          {({ id, describedBy }) => <TextInput id={id} aria-describedby={describedBy} value={name} onChange={(e) => setName(e.target.value)} required autoFocus data-testid="project-name" />}
        </Field>
        {workspace.kind === 'code' ? (
          <Field label={t('workspace.projects.rootPath')} hint={t('workspace.projects.rootPathHint')}>
            {({ id, describedBy }) => <TextInput id={id} aria-describedby={describedBy} value={rootPath} onChange={(e) => setRootPath(e.target.value)} data-testid="project-path" />}
          </Field>
        ) : null}
        <ErrorAlert error={create.error} />
      </form>
    </Dialog>
  );
}

/** "Davet et": a member of the organization joins with a role; people outside it are invited from the organization's settings. */
function InviteDialog({ workspace, onClose }: { workspace: WorkspaceDto; onClose: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const members = useWorkspaceMembers(workspace.id);
  const tenantMembers = useMembers();
  const [userId, setUserId] = useState('');
  const [role, setRole] = useState<'editor' | 'viewer'>('editor');
  const [query, setQuery] = useState('');
  const add = useMutation({
    mutationFn: () => api<WorkspaceMemberDto[]>('POST', `/api/workspaces/${workspace.id}/members`, { userId, role }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.workspaceMembers(workspace.id) });
      void queryClient.invalidateQueries({ queryKey: keys.workspaceOverview });
      onClose();
    },
  });
  const words = query.trim().toLocaleLowerCase('tr');
  const candidates = (tenantMembers.data ?? [])
    .filter((m) => !(members.data ?? []).some((w) => w.userId === m.userId))
    .filter((m) => !words || `${m.displayName} ${m.email}`.toLocaleLowerCase('tr').includes(words));
  // everyone in the organization is here already: the one way on is to invite someone to it
  const nobody = Boolean(tenantMembers.data && members.data) && candidates.length === 0 && !words;
  return (
    <Dialog
      title={t('workspace.invite.title', { name: workspace.name })}
      closeLabel={t('common.close')}
      onClose={onClose}
      data-testid="invite-dialog"
      footer={
        nobody ? (
          <Button variant="ghost" onClick={onClose}>
            {t('common.close')}
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={onClose}>
              {t('common.cancel')}
            </Button>
            <Button busy={add.isPending} disabled={!userId} onClick={() => add.mutate()} data-testid="invite-submit">
              {t('workspace.invite.add')}
            </Button>
          </>
        )
      }
    >
      {tenantMembers.isPending || members.isPending ? <Skeleton lines={3} /> : null}
      {nobody ? (
        <EmptyState
          icon={<Icons.invite />}
          title={t('workspace.invite.nobody')}
          action={
            <Link className="ui-button ui-button--secondary" to="/settings/organization" data-testid="invite-to-organization">
              {t('workspace.invite.toOrganization')}
            </Link>
          }
          data-testid="invite-nobody"
        >
          {t('workspace.invite.nobodyHint')}
        </EmptyState>
      ) : (
        <div className="dialog-form">
          <label className="search-pill">
            <Icons.search size={16} />
            <span className="ui-visually-hidden">{t('workspace.invite.search')}</span>
            <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('workspace.invite.search')} data-testid="invite-search" />
          </label>
          <fieldset className="radio-list">
            <legend className="ui-field__label">{t('workspace.invite.person')}</legend>
            {candidates.map((m) => (
              <RadioCard
                key={m.userId}
                name="invite-person"
                checked={userId === m.userId}
                onChange={() => setUserId(m.userId)}
                title={m.displayName}
                hint={m.email}
                data-testid="invite-person"
              />
            ))}
            {candidates.length === 0 ? <p className="muted">{t('sessions.noResults')}</p> : null}
          </fieldset>
          <fieldset className="radio-grid radio-grid--2">
            <legend className="ui-field__label">{t('workspace.members.role')}</legend>
            <RadioCard name="invite-role" checked={role === 'editor'} onChange={() => setRole('editor')} title={t('roles.workspace.editor')} hint={t('workspace.invite.editorHint')} />
            <RadioCard name="invite-role" checked={role === 'viewer'} onChange={() => setRole('viewer')} title={t('roles.workspace.viewer')} hint={t('workspace.invite.viewerHint')} />
          </fieldset>
          <ErrorAlert error={add.error} />
        </div>
      )}
    </Dialog>
  );
}

function ConversationsTab({ workspace, projectFilter, onProjectFilter }: { workspace: WorkspaceDto; projectFilter: string | null; onProjectFilter: (id: string | null) => void }) {
  const { t } = useTranslation();
  const conversations = useConversations(workspace.id);
  const projects = useProjects(workspace.id);
  const activity = useActivity();
  const times = useTimes();
  const detail = useRowDetail();
  const create = useNewConversation(workspace);
  const [query, setQuery] = useState('');
  const words = query.trim().toLocaleLowerCase('tr');
  const rows = (conversations.data ?? []).filter(
    (c) => (!projectFilter || c.projectId === projectFilter) && (!words || `${c.title ?? ''} ${c.summary.skills.join(' ')}`.toLocaleLowerCase('tr').includes(words)),
  );
  const chosen = projects.data?.find((p) => p.id === projectFilter);
  const canEdit = workspace.role !== 'viewer';
  return (
    <section aria-label={t('workspace.tabs.conversations')} className="workspace-main">
      <div className="workspace-main__tools">
        <label className="search-pill">
          <Icons.search size={16} />
          <span className="ui-visually-hidden">{t('workspace.conversations.search')}</span>
          <input type="search" value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t('workspace.conversations.search')} data-testid="workspace-search" />
        </label>
        {(projects.data ?? []).length > 0 ? (
          <Menu
            align="right"
            triggerClassName="ui-button ui-button--secondary"
            trigger={
              <>
                {chosen ? chosen.name : t('workspace.conversations.allProjects')}
                <Icons.chevronDown size={14} />
              </>
            }
            data-testid="workspace-project-filter"
          >
            <MenuItem icon={projectFilter === null ? <Icons.check /> : <span className="ui-icon" />} onClick={() => onProjectFilter(null)}>
              {t('workspace.conversations.allProjects')}
            </MenuItem>
            {projects.data?.map((p) => (
              <MenuItem key={p.id} icon={projectFilter === p.id ? <Icons.check /> : <Icons.workspace />} onClick={() => onProjectFilter(p.id)}>
                {p.name}
              </MenuItem>
            ))}
          </Menu>
        ) : null}
      </div>
      <ErrorAlert error={conversations.error ?? create.error} />
      {conversations.isPending ? <Skeleton lines={4} /> : null}
      {conversations.data && rows.length === 0 ? (
        words || projectFilter ? (
          <EmptyState icon={<Icons.search />} title={t('sessions.noResults')} />
        ) : (
          <EmptyState
            icon={<Icons.chat />}
            title={t('workspace.conversations.empty')}
            action={
              canEdit ? (
                <Button busy={create.isPending} onClick={() => create.mutate(undefined)} data-testid="workspace-empty-new">
                  {t('shell.newConversation')}
                </Button>
              ) : null
            }
            data-testid="workspace-conversations-empty"
          >
            {t('workspace.conversations.emptyHint')}
          </EmptyState>
        )
      ) : null}
      {rows.length > 0 ? (
        <ul className="row-list list-card" data-testid="workspace-conversations">
          {rows.map((row: ConversationRowDto) => (
            <li key={row.id}>
              <Link to={`/c/${row.id}`} className="row-link" data-testid="workspace-conversation">
                <ProfileIcon profile={row.profile} size={17} />
                <span className="row-link__text">
                  <span className="row-link__title">{row.title ?? t('conversation.untitled')}</span>
                  {detail(row, false) ? <span className="row-link__detail">{detail(row, false)}</span> : null}
                </span>
                <StatusBadge status={statusOf(activity.data, row.id)} />
                {row.summary.projectName ? <span className="row-link__tag">{row.summary.projectName}</span> : null}
                <span className="row-link__time">{times.list(row.lastActivityAt)}</span>
              </Link>
            </li>
          ))}
        </ul>
      ) : null}
    </section>
  );
}

function ProjectsList({ workspace, onPick, compact = false }: { workspace: WorkspaceDto; onPick: (id: string) => void; compact?: boolean }) {
  const { t } = useTranslation();
  const projects = useProjects(workspace.id);
  const conversations = useConversations(workspace.id);
  const count = (id: string) => (conversations.data ?? []).filter((c) => c.projectId === id).length;
  if (projects.isPending) return <Skeleton lines={2} />;
  return (
    <>
      <ErrorAlert error={projects.error} />
      <ul className={compact ? 'mini-list' : 'row-list list-card'} data-testid="project-list">
        {projects.data?.map((p) => (
          <li key={p.id}>
            <button type="button" className={compact ? 'mini-row' : 'row-link row-link--button'} onClick={() => onPick(p.id)} data-testid="project-row">
              <Icons.workspace size={17} className="mini-row__icon" />
              <span className="row-link__text">
                <span className="row-link__title">{p.name}</span>
                <span className="row-link__detail">{[t('workspaces.counts.conversations', { count: count(p.id) }), p.rootPath].filter(Boolean).join(' · ')}</span>
              </span>
            </button>
          </li>
        ))}
      </ul>
    </>
  );
}

function DocumentsList({ workspace, limit, onPreview }: { workspace: WorkspaceDto; limit?: number; onPreview: (doc: DocumentDto) => void }) {
  const { t } = useTranslation();
  const documents = useWorkspaceDocuments(workspace.id);
  const shown = limit ? (documents.data ?? []).slice(0, limit) : (documents.data ?? []);
  if (documents.isPending) return <Skeleton lines={2} />;
  return (
    <>
      <ErrorAlert error={documents.error} />
      <ul className="mini-list" data-testid="workspace-documents">
        {shown.map((doc) => (
          <li key={doc.id}>
            <button type="button" className="mini-row" onClick={() => onPreview(doc)} disabled={doc.status === 'failed'} data-testid="workspace-document">
              <KindBadge kind={doc.kind} />
              <span className="row-link__text">
                <span className="row-link__title">{doc.name}</span>
                <span className="row-link__detail">
                  {[documentSize(doc, t), formatSize(doc.size)].filter(Boolean).join(' · ')} · <DocumentState doc={doc} />
                </span>
              </span>
            </button>
          </li>
        ))}
      </ul>
    </>
  );
}

/** "Belge ekle ya da sürükleyin": files dropped or picked become documents of the workspace (DOC-007). */
function DocumentDrop({ workspace, large = false }: { workspace: WorkspaceDto; large?: boolean }) {
  const { t } = useTranslation();
  const refresh = useRefreshDocuments();
  const queryClient = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  const send = useMutation({
    mutationFn: async (files: File[]) => {
      for (const file of files) await uploadDocument(file, { scope: 'workspace', workspaceId: workspace.id });
    },
    onSuccess: () => {
      refresh();
      void queryClient.invalidateQueries({ queryKey: keys.workspaceOverview });
    },
  });
  const drop = (event: DragEvent<HTMLButtonElement>) => {
    event.preventDefault();
    setOver(false);
    const files = [...event.dataTransfer.files];
    if (files.length > 0) send.mutate(files);
  };
  return (
    <>
      <button
        type="button"
        className={['drop-button', large ? 'drop-button--large' : '', over ? 'drop-button--over' : ''].join(' ').trim()}
        onClick={() => input.current?.click()}
        onDragOver={(e) => {
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={drop}
        disabled={send.isPending}
        data-testid="workspace-document-add"
      >
        {send.isPending ? <span className="ui-spinner ui-spinner--inline" aria-hidden="true" /> : <Icons.upload size={15} />}
        {send.isPending ? t('workspace.documents.uploading') : t('workspace.documents.add')}
      </button>
      <input
        ref={input}
        type="file"
        multiple
        className="ui-visually-hidden"
        tabIndex={-1}
        aria-hidden="true"
        onChange={(e) => {
          const files = [...(e.target.files ?? [])];
          e.target.value = '';
          if (files.length > 0) send.mutate(files);
        }}
        data-testid="workspace-document-file"
      />
      <ErrorAlert error={send.error} />
    </>
  );
}

function MembersList({ workspace, manage }: { workspace: WorkspaceDto; manage: boolean }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const me = useMe();
  const members = useWorkspaceMembers(workspace.id);
  const [removing, setRemoving] = useState<WorkspaceMemberDto | null>(null);
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: keys.workspaceMembers(workspace.id) });
    void queryClient.invalidateQueries({ queryKey: keys.workspaceOverview });
  };
  const change = useMutation({ mutationFn: (input: { userId: string; role: 'editor' | 'viewer' }) => api('POST', `/api/workspaces/${workspace.id}/members`, input), onSuccess: refresh });
  const remove = useMutation({
    mutationFn: (userId: string) => api('DELETE', `/api/workspaces/${workspace.id}/members/${userId}`),
    onSuccess: () => {
      setRemoving(null);
      refresh();
    },
  });
  const isOwner = workspace.role === 'owner';
  if (members.isPending) return <Skeleton lines={3} />;
  return (
    <>
      <ErrorAlert error={members.error ?? change.error} />
      <ul className="member-list" data-testid="workspace-members">
        {members.data?.map((m) => (
          <li key={m.userId} className="member-row">
            <Avatar name={m.displayName} tone={toneOf(m.userId)} size={manage ? 'md' : 'sm'} />
            <span className="member-row__name">
              <span>
                {m.displayName} {m.userId === me.data?.user.id ? <span className="muted">({t('common.you')})</span> : null}
              </span>
              {manage ? <span className="member-row__email">{m.email}</span> : null}
            </span>
            {manage && isOwner && m.role !== 'owner' ? (
              <Menu
                align="right"
                triggerClassName="ui-button ui-button--ghost ui-button--sm"
                trigger={
                  <>
                    {t(`roles.workspace.${m.role}`)}
                    <Icons.chevronDown size={14} />
                  </>
                }
                data-testid="member-role"
              >
                <MenuItem icon={m.role === 'editor' ? <Icons.check /> : <span className="ui-icon" />} onClick={() => change.mutate({ userId: m.userId, role: 'editor' })}>
                  {t('roles.workspace.editor')}
                </MenuItem>
                <MenuItem icon={m.role === 'viewer' ? <Icons.check /> : <span className="ui-icon" />} onClick={() => change.mutate({ userId: m.userId, role: 'viewer' })}>
                  {t('roles.workspace.viewer')}
                </MenuItem>
                <MenuSeparator />
                <MenuItem danger icon={<Icons.trash />} onClick={() => setRemoving(m)} data-testid="member-remove">
                  {t('workspace.members.remove')}
                </MenuItem>
              </Menu>
            ) : (
              <Badge>{t(`roles.workspace.${m.role}`)}</Badge>
            )}
          </li>
        ))}
      </ul>
      {removing ? (
        <ConfirmDialog
          title={t('workspace.members.removeTitle', { name: removing.displayName })}
          confirmLabel={t('workspace.members.remove')}
          cancelLabel={t('common.cancel')}
          closeLabel={t('common.close')}
          busy={remove.isPending}
          error={<ErrorAlert error={remove.error} />}
          onConfirm={() => remove.mutate(removing.userId)}
          onClose={() => setRemoving(null)}
        >
          {t('workspace.members.removeBody')}
        </ConfirmDialog>
      ) : null}
    </>
  );
}

function SkillsTab({ workspace }: { workspace: WorkspaceDto }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const skills = useSkills({ workspaceId: workspace.id });
  const [adding, setAdding] = useState(false);
  const toggle = useMutation({
    mutationFn: (input: { skill: SkillDto; enabled: boolean }) => api('PUT', `/api/skills/${encodeURIComponent(input.skill.key)}/enabled`, { enabled: input.enabled, workspaceId: workspace.id }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: capKeys.skillsAll }),
  });
  const usable = (skills.data ?? []).filter((s) => (s.version !== null || s.level === 'builtin') && !s.shadowedBy);
  const own = usable.filter((s) => s.level === 'workspace' && s.workspaceId === workspace.id);
  const others = usable.filter((s) => !(s.level === 'workspace' && s.workspaceId === workspace.id));
  const canEdit = workspace.role !== 'viewer';
  const row = (skill: SkillDto) => (
    <li key={skill.key} className="skill-row" data-testid="workspace-skill" data-name={skill.name}>
      <span className="skill-row__text">
        <span className="skill-row__name">/{skill.name}</span>
        <span className="skill-row__description">{skill.description}</span>
      </span>
      <Badge>{t(`skills.levels.${skill.level}`)}</Badge>
      <Switch
        checked={skill.enabled}
        disabled={!canEdit || skill.enabledScope === 'required' || toggle.isPending}
        onChange={(e) => toggle.mutate({ skill, enabled: e.target.checked })}
        aria-label={t('workspace.skills.toggle', { name: skill.name })}
      />
    </li>
  );
  return (
    <section aria-label={t('workspace.tabs.skills')} className="workspace-tab">
      <div className="workspace-tab__head">
        <p className="muted">{t('workspace.skills.intro')}</p>
        {canEdit && own.length > 0 ? (
          <Button icon={<Icons.plus size={16} />} onClick={() => setAdding(true)} data-testid="workspace-skill-add">
            {t('skills.add.button')}
          </Button>
        ) : null}
      </div>
      <ErrorAlert error={skills.error ?? toggle.error} />
      {skills.isPending ? <Skeleton lines={4} /> : null}
      {skills.data && own.length === 0 ? (
        <EmptyState
          icon={<Icons.skills />}
          title={t('workspace.skills.empty')}
          action={
            canEdit ? (
              <Button icon={<Icons.plus size={16} />} onClick={() => setAdding(true)} data-testid="workspace-skill-add">
                {t('skills.add.button')}
              </Button>
            ) : null
          }
        >
          {t('workspace.skills.emptyHint')}
        </EmptyState>
      ) : null}
      {own.length > 0 ? (
        <>
          <h2 className="workspace-tab__title">{t('workspace.skills.own')}</h2>
          <ul className="skill-rows">{own.map(row)}</ul>
        </>
      ) : null}
      {others.length > 0 ? (
        <>
          <h2 className="workspace-tab__title">{t('workspace.skills.others')}</h2>
          <ul className="skill-rows">{others.map(row)}</ul>
        </>
      ) : null}
      {adding ? <SkillAddDialog workspaceId={workspace.id} initialScope="workspace" onClose={() => setAdding(false)} /> : null}
    </section>
  );
}

function SettingsTab({ workspace, onDelete }: { workspace: WorkspaceDto; onDelete: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [name, setName] = useState(workspace.name);
  const [description, setDescription] = useState(workspace.description ?? '');
  const save = useMutation({
    // a domain's shared workspace takes its name from the directory (Yönetim → "Kimlik doğrulama")
    mutationFn: () =>
      api<WorkspaceDto>('PATCH', `/api/workspaces/${workspace.id}`, {
        ...(workspace.directoryManaged ? {} : { name: name.trim() }),
        description: description.trim() || null,
      }),
    onSuccess: (data) => {
      queryClient.setQueryData(keys.workspace(workspace.id), data);
      void queryClient.invalidateQueries({ queryKey: keys.dashboard });
      void queryClient.invalidateQueries({ queryKey: keys.workspaceOverview });
    },
  });
  const canEdit = workspace.role !== 'viewer';
  const changed = name.trim() !== workspace.name || (description.trim() || null) !== workspace.description;
  return (
    <section aria-label={t('workspace.tabs.settings')} className="workspace-tab workspace-settings">
      <form
        className="settings-form"
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim()) save.mutate();
        }}
      >
        <Field label={t('workspace.settings.name')} {...(workspace.directoryManaged ? { hint: t('workspace.settings.directoryName') } : {})}>
          {({ id, describedBy }) => (
            <TextInput
              id={id}
              aria-describedby={describedBy}
              value={name}
              onChange={(e) => setName(e.target.value)}
              disabled={!canEdit || workspace.directoryManaged}
              required
              maxLength={120}
              data-testid="workspace-name"
            />
          )}
        </Field>
        <Field label={t('workspace.settings.description')} hint={`${description.length} / 300 · ${t('workspace.settings.descriptionHint')}`}>
          {({ id, describedBy }) => (
            <TextArea id={id} aria-describedby={describedBy} rows={3} maxLength={300} value={description} onChange={(e) => setDescription(e.target.value)} disabled={!canEdit} data-testid="workspace-description" />
          )}
        </Field>
        <ErrorAlert error={save.error} />
        {canEdit ? (
          <div className="settings-form__actions">
            {save.isSuccess && !changed ? <span className="muted">{t('workspace.settings.saved')}</span> : null}
            <Button type="submit" busy={save.isPending} disabled={!changed || !name.trim()} data-testid="workspace-save">
              {t('common.save')}
            </Button>
          </div>
        ) : null}
      </form>
      {workspace.role === 'owner' && !workspace.directoryManaged ? (
        <section className="danger-zone" aria-labelledby="workspace-danger">
          <div>
            <h2 id="workspace-danger">{t('workspace.settings.deleteTitle')}</h2>
            <p className="muted">{t('workspace.settings.deleteHint')}</p>
          </div>
          <Button variant="danger" icon={<Icons.trash size={16} />} onClick={onDelete} data-testid="workspace-delete">
            {t('workspace.settings.delete')}
          </Button>
        </section>
      ) : null}
    </section>
  );
}

export function WorkspacePage() {
  const { t } = useTranslation();
  const capabilities = useCapabilities();
  const { workspaceId = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const workspace = useWorkspace(workspaceId);
  const members = useWorkspaceMembers(workspaceId);
  const conversations = useConversations(workspaceId);
  const projects = useProjects(workspaceId);
  const documents = useWorkspaceDocuments(workspaceId);
  const skills = useSkills({ workspaceId });
  const meta = useMeta();
  const experience = useExperience();
  const [params, setParams] = useSearchParams();
  const [projectFilter, setProjectFilter] = useState<string | null>(null);
  const [dialog, setDialog] = useState<'project' | 'invite' | 'delete' | null>(null);
  const [preview, setPreview] = useState<DocumentDto | null>(null);
  // a workspace lives in its own experience (decision 2026-10-27)
  useEffect(() => {
    const kind = workspace.data?.kind;
    if (!kind || kind === experience) return;
    const origin = homeOf(meta.data, kind);
    if (origin) window.location.replace(`${origin}/w/${workspaceId}`);
  }, [workspace.data?.kind, experience, meta.data, workspaceId]);
  const create = useNewConversation(workspace.data ?? ({ id: workspaceId } as WorkspaceDto));
  const remove = useMutation({
    mutationFn: () => api('DELETE', `/api/workspaces/${workspaceId}`),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.dashboard });
      void queryClient.invalidateQueries({ queryKey: keys.workspaceOverview });
      void navigate('/workspaces');
    },
  });
  const ws = workspace.data;
  // Phase 16a: a person's own account does not share workspaces (no members, no "Davet et")
  const sharing = capabilities.organization;
  const tabs = useMemo(() => TABS.filter((tab) => (tab !== 'environment' || (ws?.kind === 'code' && ws.role !== 'viewer')) && (tab !== 'members' || sharing)), [ws?.kind, ws?.role, sharing]);
  const wanted = params.get('tab') as Tab | null;
  const tab: Tab = wanted && tabs.includes(wanted) ? wanted : 'conversations';
  const setTab = (next: Tab) =>
    setParams(
      (current) => {
        const copy = new URLSearchParams(current);
        if (next === 'conversations') copy.delete('tab');
        else copy.set('tab', next);
        return copy;
      },
      { replace: true },
    );

  if (workspace.isPending) return <PageLoading label={t('common.loading')} />;
  if (workspace.isError || !ws) {
    return (
      <div className="page">
        <ErrorAlert error={workspace.error} action={<Link to="/workspaces">{t('notFound.back')}</Link>} />
      </div>
    );
  }
  const canEdit = ws.role !== 'viewer';
  const ownSkills = (skills.data ?? []).filter((s) => s.level === 'workspace' && s.workspaceId === ws.id && s.version !== null).length;
  const count = (value: number | undefined) => (value === undefined ? null : <span className="ui-tab__count">{value}</span>);
  const label = (id: Tab) => {
    switch (id) {
      case 'conversations':
        return <>{t('workspace.tabs.conversations')} {count(conversations.data?.length)}</>;
      case 'projects':
        return <>{t('workspace.tabs.projects')} {count(projects.data?.length)}</>;
      case 'documents':
        return <>{t('workspace.tabs.documents')} {count(documents.data?.length)}</>;
      case 'skills':
        return <>{t('workspace.tabs.skills')} {count(skills.data ? ownSkills : undefined)}</>;
      case 'members':
        return <>{t('workspace.tabs.members')} {count(members.data?.length)}</>;
      default:
        return t(`workspace.tabs.${id}`);
    }
  };
  const people = (members.data ?? []).map((m) => ({ id: m.userId, name: m.displayName }));

  return (
    <div className="page page--wide workspace-page">
      <Link to="/workspaces" className="back-link" data-testid="workspace-back">
        <Icons.chevronLeft size={14} />
        {t('shell.workspaces')}
      </Link>
      <header className="workspace-header">
        <span className={`workspace-card__icon workspace-card__icon--${ws.kind} workspace-header__icon`} aria-hidden="true">
          {ws.kind === 'code' ? <Icons.code size={22} /> : <Icons.chat size={22} />}
        </span>
        <div className="workspace-header__text">
          <div className="workspace-header__title">
            <h1 data-testid="workspace-title">{ws.name}</h1>
            <span className={`workspace-kind workspace-kind--${ws.kind}`}>{t(`kinds.${ws.kind}`)}</span>
          </div>
          {ws.description ? <p className="workspace-header__description">{ws.description}</p> : null}
        </div>
        <div className="workspace-header__actions">
          {people.length > 0 ? <AvatarStack people={people} size="md" label={t('workspaces.members', { count: people.length })} /> : null}
          {ws.role === 'owner' && sharing ? (
            <Button variant="secondary" icon={<Icons.invite size={16} />} onClick={() => setDialog('invite')} data-testid="workspace-invite">
              {t('workspace.invite.button')}
            </Button>
          ) : null}
          {canEdit ? (
            <Button icon={<Icons.plus size={16} />} busy={create.isPending} onClick={() => create.mutate(projectFilter ?? undefined)} data-testid="new-conversation">
              {t('shell.newConversation')}
            </Button>
          ) : null}
          <Menu align="right" label={t('workspace.actions')} triggerClassName="ui-icon-button ui-icon-button--outlined" trigger={<Icons.more />} data-testid="workspace-menu">
            {canEdit ? (
              <MenuItem icon={<Icons.plus />} onClick={() => setDialog('project')} data-testid="menu-new-project">
                {t('workspace.projects.new')}
              </MenuItem>
            ) : null}
            <MenuItem icon={<Icons.settings />} onClick={() => setTab('settings')}>
              {t('workspace.tabs.settings')}
            </MenuItem>
            {ws.role === 'owner' && !ws.directoryManaged ? (
              <>
                <MenuSeparator />
                <MenuItem danger icon={<Icons.trash />} onClick={() => setDialog('delete')}>
                  {t('workspace.settings.delete')}
                </MenuItem>
              </>
            ) : null}
          </Menu>
        </div>
      </header>
      <ErrorAlert error={create.error} />

      <Tabs tabsId={TABS_ID} variant="underline" label={t('workspace.tabsLabel')} value={tab} onChange={setTab} tabs={tabs.map((id) => ({ id, label: label(id), 'data-testid': `workspace-tab-${id}` }))} />

      <TabPanel tabsId={TABS_ID} id={tab} className="workspace-panel">
        {tab === 'conversations' ? (
          <div className="workspace-layout">
            <ConversationsTab workspace={ws} projectFilter={projectFilter} onProjectFilter={setProjectFilter} />
            <aside className="workspace-aside">
              <section className="side-box" aria-labelledby="side-projects">
                <div className="side-box__head">
                  <h2 id="side-projects">{t('workspace.tabs.projects')}</h2>
                  {canEdit ? (
                    <Button size="sm" variant="secondary" icon={<Icons.plus size={14} />} onClick={() => setDialog('project')} data-testid="project-new">
                      {t('workspace.projects.new')}
                    </Button>
                  ) : null}
                </div>
                {projects.data?.length === 0 ? <p className="side-box__empty">{t('workspace.projects.emptyShort')}</p> : null}
                <ProjectsList workspace={ws} compact onPick={(id) => setProjectFilter(projectFilter === id ? null : id)} />
              </section>
              <section className="side-box" aria-labelledby="side-documents">
                <div className="side-box__head">
                  <h2 id="side-documents">{t('workspace.documents.title')}</h2>
                  {(documents.data?.length ?? 0) > 0 ? (
                    <button type="button" className="side-box__more" onClick={() => setTab('documents')}>
                      {t('workspace.documents.all', { count: documents.data?.length ?? 0 })}
                    </button>
                  ) : null}
                </div>
                <DocumentsList workspace={ws} limit={3} onPreview={setPreview} />
                {canEdit ? <DocumentDrop workspace={ws} /> : null}
              </section>
              <section className="side-box" aria-labelledby="side-members">
                <div className="side-box__head">
                  <h2 id="side-members">{t('workspace.tabs.members')}</h2>
                </div>
                <MembersList workspace={ws} manage={false} />
              </section>
            </aside>
          </div>
        ) : null}

        {tab === 'projects' ? (
          <section className="workspace-tab" aria-label={t('workspace.tabs.projects')}>
            <div className="workspace-tab__head">
              <p className="muted">{t(ws.kind === 'code' ? 'workspace.projects.codeHint' : 'workspace.projects.chatHint')}</p>
              {canEdit && (projects.data?.length ?? 0) > 0 ? (
                <Button icon={<Icons.plus size={16} />} onClick={() => setDialog('project')} data-testid="project-new">
                  {t('workspace.projects.new')}
                </Button>
              ) : null}
            </div>
            {projects.data?.length === 0 ? (
              <EmptyState
                icon={<Icons.workspace />}
                title={t('workspace.projects.empty')}
                action={
                  canEdit ? (
                    <Button icon={<Icons.plus size={16} />} onClick={() => setDialog('project')} data-testid="project-new">
                      {t('workspace.projects.new')}
                    </Button>
                  ) : null
                }
                data-testid="projects-empty"
              >
                {t('workspace.projects.emptyHint')}
              </EmptyState>
            ) : (
              <ProjectsList
                workspace={ws}
                onPick={(id) => {
                  setProjectFilter(id);
                  setTab('conversations');
                }}
              />
            )}
          </section>
        ) : null}

        {tab === 'environment' ? (
          <div className="ui-card workspace-env">
            <SandboxPanel workspace={ws} />
          </div>
        ) : null}

        {tab === 'documents' ? (
          <section className="workspace-tab" aria-label={t('workspace.tabs.documents')}>
            <div className="workspace-tab__head">
              <p className="muted">{t('workspace.documents.intro')}</p>
            </div>
            {documents.data?.length === 0 ? (
              <EmptyState icon={<Icons.documents />} title={t('workspace.documents.empty')} action={canEdit ? <DocumentDrop workspace={ws} large /> : null} data-testid="documents-empty">
                {t('workspace.documents.emptyHint')}
              </EmptyState>
            ) : (
              <>
                <DocumentsList workspace={ws} onPreview={setPreview} />
                {canEdit ? <DocumentDrop workspace={ws} large /> : null}
              </>
            )}
          </section>
        ) : null}

        {tab === 'skills' ? <SkillsTab workspace={ws} /> : null}

        {tab === 'members' ? (
          <section className="workspace-tab" aria-label={t('workspace.tabs.members')}>
            <div className="workspace-tab__head">
              <p className="muted">{t('workspace.members.intro')}</p>
              {ws.role === 'owner' ? (
                <Button icon={<Icons.invite size={16} />} onClick={() => setDialog('invite')}>
                  {t('workspace.invite.button')}
                </Button>
              ) : null}
            </div>
            <MembersList workspace={ws} manage />
          </section>
        ) : null}

        {tab === 'settings' ? <SettingsTab key={ws.updatedAt} workspace={ws} onDelete={() => setDialog('delete')} /> : null}
      </TabPanel>

      {dialog === 'project' ? <ProjectDialog workspace={ws} onClose={() => setDialog(null)} /> : null}
      {dialog === 'invite' ? <InviteDialog workspace={ws} onClose={() => setDialog(null)} /> : null}
      {dialog === 'delete' ? (
        <ConfirmDialog
          title={t('workspace.settings.deleteConfirmTitle', { name: ws.name })}
          confirmLabel={t('workspace.settings.delete')}
          cancelLabel={t('common.cancel')}
          closeLabel={t('common.close')}
          busy={remove.isPending}
          error={<ErrorAlert error={remove.error} />}
          onConfirm={() => remove.mutate()}
          onClose={() => setDialog(null)}
          data-testid="workspace-delete-dialog"
        >
          {t('workspace.deleteConfirm')}
        </ConfirmDialog>
      ) : null}
      {preview ? (
        <Dialog title={preview.name} closeLabel={t('common.close')} onClose={() => setPreview(null)} size="full" data-testid="workspace-document-preview">
          <DocumentPreview doc={(documents.data ?? []).find((d) => d.id === preview.id) ?? preview} focus={null} />
        </Dialog>
      ) : null}
    </div>
  );
}
